import { useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { useInViewOnce } from './useInViewOnce'
import MediaLightbox from './MediaLightbox'
import VideoFramePreview from './VideoFramePreview'
const EASE = [0.16, 1, 0.3, 1]
function VideoSection({ videos }) {
  const [scrollRef, nearViewport] = useInViewOnce({ rootMargin: '280px' })
  const [openIndex, setOpenIndex] = useState(null)
  const reduceMotion = useReducedMotion()
  if (!videos.length) return null
  const media =
    openIndex == null
      ? null
      : {
          kind: 'video',
          url: videos[openIndex].url,
          caption: videos[openIndex].label,
        }
  return (
    <>
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: reduceMotion ? 0.2 : 0.9, ease: EASE }}
        className="relative border-t border-m1-line-soft overflow-hidden"
      >
        {/* Deep cinematic base */}
        <div className="absolute inset-0 bg-m1-ground" aria-hidden="true" />
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 20% 0%, rgba(180,156,245,0.16) 0%, transparent 55%), radial-gradient(ellipse 60% 45% at 90% 80%, rgba(143,216,230,0.10) 0%, transparent 50%)',
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-m1-ink/20 to-transparent"
          aria-hidden="true"
        />

        <div className="relative py-[var(--section)]">
          {/* Header */}
          <div className="max-w-[1200px] mx-auto px-[var(--gutter)]">
            <motion.div
              initial={{ opacity: 0, y: reduceMotion ? 0 : 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: reduceMotion ? 0.2 : 0.8, ease: EASE }}
            >
              <p className="text-[14px] text-m1-muted">On camera</p>
              <h2 className="mt-4 max-w-4xl font-m1-display text-[clamp(40px,5.4vw,72px)] font-medium sm:font-normal leading-none tracking-[-0.015em] text-m1-ink">
                Every angle.
                <span className="block text-m1-muted">Captured in the lab.</span>
              </h2>
              <p className="mt-6 max-w-[32em] text-[19px] font-light leading-relaxed text-m1-ink-soft">
                Inspection and grading footage, minted with the token. Playback begins only when you
                choose a clip.
              </p>
            </motion.div>
          </div>

          {/* Video wall: every clip is visible at once instead of hiding behind a swipe. */}
          <div ref={scrollRef} className="mx-auto mt-12 max-w-[1200px] px-[var(--gutter)] sm:mt-16">
            <div
              className={`grid grid-cols-1 gap-4 sm:gap-6 ${videos.length > 1 ? 'md:grid-cols-2' : ''}`}
              role="list"
            >
              {videos.map((video, index) => {
                const previewEnabled = nearViewport
                const reelNumber = String(index + 1).padStart(2, '0')
                const reelTotal = String(videos.length).padStart(2, '0')
                // With an odd count the first clip leads the wall across both columns.
                const featured = videos.length > 2 && videos.length % 2 === 1 && index === 0
                return (
                  <motion.button
                    key={video.url}
                    type="button"
                    role="listitem"
                    initial={{ opacity: 0, y: reduceMotion ? 0 : 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{
                      duration: reduceMotion ? 0.2 : 0.75,
                      delay: reduceMotion ? 0 : (index % 2) * 0.08,
                      ease: EASE,
                    }}
                    onClick={() => setOpenIndex(index)}
                    aria-label={`Play ${video.label} video ${index + 1}`}
                    className={`group relative min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-m1-violet focus-visible:ring-offset-2 focus-visible:ring-offset-m1-ground ${
                      featured ? 'md:col-span-2' : ''
                    }`}
                  >
                    <div
                      className={`relative overflow-hidden rounded-2xl bg-m1-surface shadow-[0_40px_80px_-24px_rgba(0,0,0,0.85)] ring-1 ring-m1-line-soft transition-all duration-500 group-hover:ring-m1-violet/50 group-hover:shadow-[0_48px_96px_-20px_rgba(180,156,245,0.18)] ${
                        featured ? 'aspect-video md:aspect-[21/9]' : 'aspect-video'
                      }`}
                    >
                      <VideoFramePreview src={video.url} enabled={previewEnabled} />

                      {/* Cinematic grade overlay */}
                      <div
                        className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/30 pointer-events-none"
                        aria-hidden="true"
                      />
                      <div
                        className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 pointer-events-none"
                        style={{
                          background:
                            'radial-gradient(circle at 50% 50%, rgba(180,156,245,0.12) 0%, transparent 65%)',
                        }}
                        aria-hidden="true"
                      />

                      {/* Top meta */}
                      <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-3 p-4 sm:p-5 pointer-events-none">
                        <span className="bg-m1-ground/60 px-3 py-1 text-[13px] tracking-[0.04em] text-m1-ink backdrop-blur-md ring-1 ring-m1-line">
                          {video.label}
                        </span>
                        <span className="font-m1-mono text-[13px] text-m1-ink/60 tabular-nums">
                          {reelNumber}
                          <span className="text-m1-ink/25"> / </span>
                          {reelTotal}
                        </span>
                      </div>

                      {/* Play control — scales in on hover */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="flex h-14 w-14 sm:h-[4.5rem] sm:w-[4.5rem] items-center justify-center rounded-full bg-white/10 backdrop-blur-md ring-1 ring-white/25 transition-all duration-500 scale-90 opacity-80 group-hover:scale-100 group-hover:opacity-100 group-hover:bg-white/15 group-hover:ring-m1-violet/60">
                          <i className="ri-play-fill text-2xl sm:text-3xl text-white translate-x-[2px]" />
                        </span>
                      </div>

                      {/* Bottom caption rail */}
                      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-4 sm:p-5 pointer-events-none">
                        <div>
                          <p className="font-m1-mono text-[13px] text-m1-violet">Tap to play</p>
                          <p className="mt-1 text-[17px] text-m1-ink">{video.label} footage</p>
                        </div>
                        <i
                          className="ri-arrow-right-line text-xl text-m1-ink/30 transition-all duration-300 group-hover:text-m1-violet group-hover:translate-x-1"
                          aria-hidden="true"
                        />
                      </div>

                      {/* Edge highlight on hover */}
                      <div
                        className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-m1-violet/60 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                        aria-hidden="true"
                      />
                    </div>
                  </motion.button>
                )
              })}
            </div>
          </div>
        </div>
      </motion.section>

      <MediaLightbox media={media} onClose={() => setOpenIndex(null)} />
    </>
  )
}
export { VideoSection as default }
